import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AppConfig } from 'src/app/config/config';
import { AccountKycList, OnboardedCustomersList, ServiceRequestTrackerItem } from 'src/app/models/accountKyc.model';
import { KycAccountDetails } from 'src/app/models/kyc.model';
import { ConfirmPaymentDataIn } from 'src/app/models/serviceConnection.model';
import { SetNewPassword } from 'src/app/models/setNewPassword.model';
import { UserActivity } from 'src/app/models/userActivity.model';
import { PaginationDto, ResponsePackage } from 'src/app/models/util.model';

@Injectable({
  providedIn: 'root',
})
export class AccountKycService {
  private pathApi = this.config.setting['pathApi'] + 'kyc/account/';
  private dataApi = this.config.setting['pathApi'] + 'kyc/DataKyc/';
  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  constructor(private http: HttpClient, private config: AppConfig) { }

  login(obj: any) {
    return this.http.post<any>(`${this.pathApi}login`, obj, {
      headers: this.headers,
      observe: 'response',
    });
  }
  register(obj: any) {
    return this.http.post<any>(`${this.pathApi}register`, obj, {
      headers: this.headers,
      observe: 'response',
    });
  }
  checkAccountNumber(obj: any) {
    return this.http.post<any>(`${this.pathApi}checkAccountNumber`, obj, {
      headers: this.headers,
      observe: 'response',
    });
  }
  checkAccountOrMeterNumber(obj: any) {
    return this.http.post<any>(`${this.pathApi}checkAccountOrMeterNumber`, obj, {
      headers: this.headers,
      observe: 'response',
    });
  }
  sendEmail(obj: any): Observable<any> {
    return this.http.post(
      `${this.pathApi}sendResetPasswordEmail`, obj

    );
  }

  setNewPassword(newPasswordRequest: SetNewPassword): Observable<any> {
    return this.http.post(`${this.pathApi}setNewPassword`, newPasswordRequest, {
      headers: this.headers,
      observe: 'response',
    });
  }
  getAll(obj: any) {
    return this.http.post<ResponsePackage<PaginationDto<AccountKycList>>>(
      `${this.pathApi}getAll`,
      obj,
      { headers: this.headers, observe: 'response' }
    );
  }

  getOnboardedCustomers(obj: any){
    return this.http.post<ResponsePackage<PaginationDto<OnboardedCustomersList>>>(
      `${this.pathApi}getOnboardedCustomers`,
      obj,
      { headers: this.headers, observe: 'response' }
    );
  }

  getAllServiceConnection(obj: any) {
    return this.http.post<ResponsePackage<PaginationDto<AccountKycList>>>(
      `${this.pathApi}getAllServiceConnection`,
      obj,
      { headers: this.headers, observe: 'response' }
    );
  }

  getAllServiceConnectionCount(obj: any) {
    return this.http.post<ResponsePackage<number>>(
      `${this.pathApi}getAllServiceConnectionCount`,
      obj,
      { headers: this.headers, observe: 'response' }
    );
  }

  download(obj: any): Observable<any> {
    return this.http.post(`${this.pathApi}downloadFile`, obj, {
      headers: this.headers,
      observe: 'response',
    });
  }

  downloadServiceConnection(obj: any): Observable<any> {
    return this.http.post(`${this.pathApi}downloadServiceConnection`, obj, {
      headers: this.headers,
      observe: 'response',
    });
  }

  sendKycEmail(id: number): Observable<any> {
    return this.http.post(`${this.pathApi}sendKycEmail?id=${id}`, null);
  }

  getKycAccountById(id: number) {
    return this.http.get<any>(`${this.dataApi}getKycAccountDetails?id=${id}`);
  }

  getUserActivity(kycAccountId: number, timezoneValue: string): Observable<ResponsePackage<UserActivity>> {
    return this.http.get<ResponsePackage<UserActivity>>(
      `${this.dataApi}getUserActivity?kycAccountId=${kycAccountId}&timezoneValue=${timezoneValue}`
    );
  }

  approveKycAccount(id: number): Observable<any> {
    return this.http.post(`${this.pathApi}approveKycAccount`, id);
  }

  suspendKycAccount(id: number): Observable<any> {
    return this.http.post(`${this.pathApi}suspendKycAccount`, id);
  }

  recallKycAccount(id: number): Observable<any> {
    return this.http.post(`${this.pathApi}recallKycAccount`, id);
  }

  saveKycRejection(obj: any): Observable<any> {
    return this.http.post(`${this.dataApi}saveKYCRejection`, obj);
  }

  editKycAccount(obj: KycAccountDetails): Observable<any> {
    return this.http.post(`${this.dataApi}editKycAccount`, obj);
  }

  getServiceConnectionById(id: number) {
    return this.http.get<any>(`${this.pathApi}getServiceConnectionDetails?id=${id}`);
  }

  getServiceRequestTrackerDetails(id: number) {
    return this.http.get<any>(`${this.pathApi}getServiceRequestTrackerDetails?id=${id}`);
  }

  confirmPayment(dataIn: ConfirmPaymentDataIn) {
    return this.http.post<any>(`${this.pathApi}confirmPayment`, dataIn);
  }

  ninVerification(obj: any): Observable<any> {
    return this.http.post(`${this.pathApi}ninVerification`, obj, {
      headers: this.headers,
      observe: 'response',
    });
  }

  getNinVerificationData(accountKycId: number): Observable<any> {
    return this.http.get<any>(`${this.pathApi}getNinVerificationData?accountKycId=${accountKycId}`);
  }

  verifyOtp(obj: any) {
    return this.http.post<any>(`${this.pathApi}verifyOtp`, obj, {
      headers: this.headers,
      observe: 'response',
    });
  }

  resendOtp(obj: any) {
    return this.http.post<any>(`${this.pathApi}resendOtp`, obj, {
      headers: this.headers,
      observe: 'response',
    });
  }
  generateTemplate(utilityId: number): Observable<any> {
    return this.http.get(
      `${this.pathApi}generateTemplate?utilityId=${utilityId}`,
    );
  }
  uploadFile(obj: any, utilityId: number): Observable<any> {
    return this.http.post(`${this.pathApi}uploadFile?utilityId=${utilityId}`, obj);
  }
  generateBulkVendorAssignmentTemplate(utilityId: number): Observable<any> {
    return this.http.get(
      `${this.pathApi}generateBulkVendorAssignmentTemplate?utilityId=${utilityId}`,
    );
  }
  uploadBulkVendorAssignment(obj: any, utilityId: number): Observable<any> {
    return this.http.post(`${this.pathApi}uploadBulkVendorAssignment?utilityId=${utilityId}`, obj);
  }
  generateBulkConfirmPaymentTemplate(utilityId: number): Observable<any> {
    return this.http.get(
      `${this.pathApi}generateBulkConfirmPaymentTemplate?utilityId=${utilityId}`,
    );
  }
  uploadBulkConfirmPayment(obj: any, utilityId: number): Observable<any> {
    return this.http.post(`${this.pathApi}uploadBulkConfirmPayment?utilityId=${utilityId}`, obj);
  }

  getAllServiceRequestTracker(obj: any){
    return this.http.post<ResponsePackage<PaginationDto<ServiceRequestTrackerItem>>>(
      `${this.pathApi}getAllServiceRequestTracker`,
      obj,
      { headers: this.headers, observe: 'response' }
    );
  }

  getAllServiceRequestTrackerCount(obj: any){
    return this.http.post<ResponsePackage<number>>(
      `${this.pathApi}getAllServiceRequestTrackerCount`,
      obj,
      { headers: this.headers, observe: 'response' }
    );
  }
}
